import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { Callout } from '@/components/ui/callout';
import { useTranslation } from '@/hooks/useTranslation';
import {
  formatNumber,
  formatProbability,
  formatWear,
  listFields,
  missingFields,
  plural,
} from '../lib/wearFormat';
import type { WearOutOfRange, WearOutput, WearTrace } from '../type/WearTypes';

const DIGITS: Record<string, number> = { ap: 2, ae: 2, diameter: 1, hm: 1 };

function formatValues(range: WearOutOfRange, language: string): { values: string; fitted: string } {
  if (range.field === 'material') {
    return {
      values: range.values.join(', '),
      fitted: (range.fitted ?? []).join(', ') || '—',
    };
  }
  const digits = DIGITS[range.field] ?? 2;
  const num = (value: number | string) =>
    typeof value === 'number' ? formatNumber(language, value, digits) : value;
  const fitted = range.fitted ?? [];
  const fittedText =
    fitted.length === 2 && fitted[0] !== fitted[1]
      ? `${num(fitted[0])}–${num(fitted[1])}`
      : fitted.length > 0
        ? String(num(fitted[0]))
        : '—';
  return { values: range.values.map(num).join(', '), fitted: fittedText };
}

/**
 * Traces the model could not count (no ae, fz or volume): their volume is missing from the
 * wear, so the wear is a minimum and what the tool can still mill is overestimated.
 */
const IncompleteTracesBanner: FC<{ output: WearOutput; skipped: WearTrace[] }> = ({
  output,
  skipped,
}) => {
  const { t, language } = useTranslation();
  const title =
    output.counted === 0
      ? plural(t, 'mlWear.result.incomplete.titleNone', skipped.length)
      : plural(t, 'mlWear.result.incomplete.titleSome', skipped.length, {
          total: output.traces.length,
        });
  const volumes = skipped.flatMap((trace) => (trace.volume === null ? [] : [trace.volume]));
  const pieces = skipped.reduce((sum, trace) => sum + (trace.pieces ?? 0), 0);
  const amounts = [
    ...(volumes.length > 0
      ? [
          `${formatNumber(
            language,
            volumes.reduce((sum, v) => sum + v, 0),
            2
          )} cm³`,
        ]
      : []),
    ...(pieces > 0 ? [plural(t, 'mlWear.result.milledPieces', pieces)] : []),
  ];

  const why = (trace: WearTrace) => {
    const fields = missingFields(trace);
    return fields.length > 0
      ? plural(t, 'mlWear.result.incomplete.missing', fields.length, {
          fields: listFields(language, fields),
        })
      : t(`mlWear.result.skippedReasons.${trace.skippedReason}`);
  };

  return (
    <Callout tone="warning" data-testid="wear-incomplete-traces">
      <div className="flex flex-col gap-1">
        <p>
          <span className="font-semibold">{title}</span>
          {amounts.length > 0 && ` (${amounts.join(', ')})`}.{' '}
          {t('mlWear.result.incomplete.consequence')}
        </p>
        <ul className="list-disc pl-5">
          {skipped.map((trace, index) => (
            <li key={trace.traceId ?? index}>
              {trace.date ? new Date(trace.date).toLocaleDateString(language) : '—'} ·{' '}
              {trace.operation ?? '—'} ·{' '}
              {trace.volume === null ? '—' : `${formatNumber(language, trace.volume, 2)} cm³`} —{' '}
              <span className="font-semibold text-warning">{why(trace)}</span>
            </li>
          ))}
        </ul>
        <Link
          to={`/traceability/tools/${encodeURIComponent(output.toolId)}`}
          className="w-fit font-medium text-primary underline-offset-2 hover:underline"
        >
          {t('mlWear.result.incomplete.link')}
        </Link>
      </div>
    </Callout>
  );
};

/** Reported-worn, incomplete-traces and extrapolation notices above the result cards. */
export const WearBanners: FC<{ output: WearOutput }> = ({ output }) => {
  const { t, language } = useTranslation();
  const newest = output.traces[output.traces.length - 1];
  const terminal = newest && (newest.toolState === 'worn' || newest.toolState === 'broken');
  const date = newest?.date ? new Date(newest.date).toLocaleDateString(language) : '—';
  const skipped = output.traces.filter((trace) => trace.skippedReason !== null);

  return (
    <>
      {terminal && (
        <Callout tone="error" role={null} data-testid="wear-reported-worn">
          <span className="font-semibold">
            {t(
              newest.toolState === 'worn'
                ? 'mlWear.result.reportedWorn'
                : 'mlWear.result.reportedBroken',
              {
                date,
              }
            )}
          </span>{' '}
          {t('mlWear.result.reportedDetail', {
            wear: formatWear(language, output.wear),
            pWorn: formatProbability(language, output.pWorn),
          })}
        </Callout>
      )}
      {skipped.length > 0 && <IncompleteTracesBanner output={output} skipped={skipped} />}
      {output.outOfRange.length > 0 && (
        <Callout
          tone="warning"
          title={t('mlWear.result.outOfRangeTitle')}
          data-testid="wear-out-of-range"
        >
          <ul className="mt-1 list-disc pl-5">
            {output.outOfRange.map((range) => (
              <li key={range.field}>
                {t(`mlWear.result.outOfRange.${range.field}`, formatValues(range, language))}
              </li>
            ))}
          </ul>
        </Callout>
      )}
    </>
  );
};
